import React, { useCallback, useState } from "react";
import { Platform, Pressable, ScrollView, StyleSheet } from "react-native";
import Animated, {
  FadeIn,
  FadeInDown,
  FadeOut,
  FadeOutDown,
} from "react-native-reanimated";
import { Button } from "~/components/Button";
import { Spacer } from "~/components/Spacer";
import { Text } from "~/components/Text";
import { View } from "~/components/View";
import { styleConstants } from "~/style/styleConstants";
import { Icon } from "../Icon";
import { Modal } from "../Modal";
import { AlertContext } from "./AlertContext";
import type { ContextType } from "./AlertContext";
import type { IconProps } from "../Icon";
import type { AlertButton } from "react-native";

const defaultOptions = {
  cancelable: false,
  onDismiss: () => {},
  buttonsContainerStyle: {},
};

const S = StyleSheet.create({
  flex: { flex: 1 },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: styleConstants.colorBackgroundBackdrop,
  },

  popup: {
    maxHeight: "85%",
    width: "80%",
    maxWidth: 520,
    borderRadius: 4,
    backgroundColor: styleConstants.colorBackgroundWhite,
    padding: styleConstants.spacingLarge,
  },
});

interface AlertProviderProps {
  children: React.ReactNode;
}

export function AlertProvider({ children, ...otherProps }: AlertProviderProps) {
  const [alertVisible, setAlertVisible] = useState(false);
  const [alertParams, setAlertParams] = useState({
    title: "",
    body: "",
    buttons: [] as AlertButton[],
    options: defaultOptions,
    iconName: "" as IconProps["name"] | undefined,
  });

  const alert: ContextType["alert"] = useCallback(
    (
      title = "",
      body = "",
      buttons = [],
      iconName,
      options = defaultOptions,
    ) => {
      const preparedOptions = { ...defaultOptions, ...options };
      setAlertParams({
        title,
        body,
        buttons,
        options: preparedOptions,
        iconName,
      });
      setAlertVisible(true);
    },
    [],
  );

  const contextValue = { alertVisible, setAlertVisible, alert };

  const { title, body, buttons, options, iconName } = alertParams;

  const { onDismiss, cancelable, buttonsContainerStyle } = options;

  const handleModalPress = () => {
    if (cancelable) {
      onDismiss();
      setAlertVisible(false);
    }
  };

  const flatStyle = StyleSheet.flatten(buttonsContainerStyle);
  const isColumnLayout =
    buttons.length > 2 ||
    ("flexDirection" in flatStyle && flatStyle.flexDirection === "column");

  const renderedButtons = buttons.map((button, index) => {
    return (
      <React.Fragment key={button.text}>
        {index !== 0 && <Spacer medium />}
        <Button
          outline={button.style === "cancel"}
          title={button.text}
          onPress={() => {
            setAlertVisible(false);
            return button.onPress?.();
          }}
          style={
            isColumnLayout
              ? {
                  minWidth: 240,
                  width: "50%",
                  maxWidth: 400,
                  alignSelf: "center",
                }
              : {
                  minWidth: 100,
                  width: "50%",
                  maxWidth: 400,
                  alignSelf: "center",
                }
          }
          colorDanger={button.style === "destructive"}
        />
      </React.Fragment>
    );
  });

  return (
    <AlertContext.Provider value={contextValue} {...otherProps}>
      {children}
      {alertVisible && (
        <Modal>
          <Animated.View style={S.backdrop} entering={FadeIn} exiting={FadeOut}>
            <Pressable
              style={S.flex}
              onPress={handleModalPress}
              disabled={!cancelable}
            />
          </Animated.View>
          <View
            flex
            centerContent
            pointerEvents={Platform.OS === "web" ? "none" : "box-none"}
          >
            {/* Popup / Modal container */}
            <Animated.View
              entering={FadeInDown}
              exiting={FadeOutDown}
              pointerEvents="auto"
              style={S.popup}
            >
              {iconName && (
                <View centerContent>
                  <Icon name={iconName} />
                </View>
              )}
              <ScrollView bounces={false} showsVerticalScrollIndicator={false}>
                {!!title && (
                  <View paddingVerticalMedium>
                    <Text weightExtraBold sizeLarge alignCenter>
                      {title}
                    </Text>
                  </View>
                )}

                {!!body && (
                  <View paddingVerticalMedium>
                    {typeof body === "string" ? (
                      <Text alignCenter sizeLarge weightSemiBold>
                        {body}
                      </Text>
                    ) : (
                      <>{body}</>
                    )}
                  </View>
                )}
              </ScrollView>

              <Spacer />

              <View
                flexDirectionRow={!isColumnLayout}
                justifyContentCenter={!isColumnLayout}
                style={buttonsContainerStyle}
              >
                {renderedButtons.length === 0 ? (
                  <Button
                    transparent
                    title={intl.formatMessage({
                      id: "C3dd3n",
                      defaultMessage: "OK",
                      description: "Alert button",
                    })}
                    onPress={() => {
                      setAlertVisible(false);
                    }}
                    style={{
                      minWidth: 100,
                      width: "50%",
                      maxWidth: 400,
                      alignSelf: "center",
                    }}
                  />
                ) : (
                  renderedButtons
                )}
              </View>
            </Animated.View>
          </View>
        </Modal>
      )}
    </AlertContext.Provider>
  );
}
